import type { CardType } from './card';

/**
 * 매입 오더 상태. 백엔드 BuybackStatus 와 1:1.
 *   신규 매입 → 매입 준비중 → 매입 준비 완료 → 매입 완료 / 매입 불성립
 */
export type BuybackStatus = 'NEW_ORDER' | 'PREPARING' | 'READY' | 'COMPLETED' | 'CANCELLED';

export const BUYBACK_STATUS_LABEL: Record<BuybackStatus, string> = {
  NEW_ORDER: '신규 매입',
  PREPARING: '매입 준비중',
  READY:     '매입 준비 완료',
  COMPLETED: '매입 완료',
  CANCELLED: '매입 불성립',
};

/** 상태 필터 탭에 노출하는 순서. */
export const BUYBACK_STATUS_ORDER: BuybackStatus[] =
  ['NEW_ORDER', 'PREPARING', 'READY', 'COMPLETED', 'CANCELLED'];

/**
 * 카드 상태 등급. 백엔드 CardGrade 와 1:1(enum 이름은 '-' 를 못 써 A_MINUS 형태).
 * 상사 API 매입가는 S 기준이고, 등급에 따른 금액 조정은 관리자가 단가를 직접 수정해 반영한다
 * (등급 자체는 금액을 자동으로 바꾸지 않는다). 매장 내부 정보라 고객 안내 앱에는 내려오지 않는다.
 */
export type CardGrade = 'S' | 'S_MINUS' | 'A' | 'A_MINUS' | 'B' | 'B_MINUS';

/** 드롭다운·표기 순서(높은 등급부터). */
export const CARD_GRADE_ORDER: CardGrade[] = ['S', 'S_MINUS', 'A', 'A_MINUS', 'B', 'B_MINUS'];

/** 화면 표기용 라벨("S-" 등). */
export const CARD_GRADE_LABEL: Record<CardGrade, string> = {
  S:       'S',
  S_MINUS: 'S-',
  A:       'A',
  A_MINUS: 'A-',
  B:       'B',
  B_MINUS: 'B-',
};

/**
 * 매입 관리 목록 한 줄. 성립/불성립 오더는 개인정보가 삭제돼 name/phone/email 이 null.
 */
export interface Buyback {
  id:               number;
  /** 사람이 읽는 관리 번호(예: "20260730-001", 접수 순번). 도입 이전 오더는 null. */
  managementNumber: string | null;
  name:             string | null;
  phone:            string | null;
  email:            string | null;
  status:           BuybackStatus;
  statusLabel:      string;
  itemCount:        number;
  totalAmount:      number | null;
  createdAt:        string; // ISO 문자열
}

/**
 * 매입 오더 명세 한 줄. cardId 는 기존 재고 카드에 매칭됐을 때만 존재(수기 카드는 null).
 * 같은 카드라도 매입 가능/불가능을 나누려면 buyable 이 다른 두 줄로 분리한다.
 */
export interface BuybackItem {
  id:                number | null; // 신규 줄은 저장 전이라 null
  cardId:            number | null;
  productName:       string;
  productNumber:     string | null;
  sellerProductCode: string | null;
  cardType:          CardType | null;
  imageUrl:          string | null;
  quantity:          number;
  unitPrice:         number | null;
  /** 카드 상태 등급. 관리자 응답에만 존재(고객 안내 앱 응답에는 null). 도입 이전 명세도 null. */
  grade:             CardGrade | null;
  buyable:           boolean;
  customerSelected:  boolean;
}

/** 카트 저장(PUT items) 시 백엔드로 보내는 명세 입력. */
export type BuybackItemInput = Omit<BuybackItem, 'id' | 'customerSelected'> & {
  id?: number | null;
  customerSelected?: boolean;
};

/**
 * 매입 오더에 첨부한 실물 카드 사진의 메타(바이트 제외). 실제 이미지는
 * GET /api/buybacks/{id}/photos/{photoId} 로 스트리밍한다(인증 필요).
 */
export interface BuybackPhoto {
  id:           number;
  contentType:  string;
  originalName: string | null;
  sizeBytes:    number;
  createdAt:    string; // ISO 문자열
}

/**
 * 매입 영수증 데이터(개인정보 없음). 완료된 오더의 매장 정보·관리번호·내역·총액.
 * 등급은 내부 정보라 영수증에는 담기지 않는다. 관리자 인쇄(방식②)에서 사용.
 */
export interface BuybackReceipt {
  shop: {
    name:           string;
    businessNumber: string;
    owner:          string;
    address:        string;
    phone:          string;
  };
  managementNumber: string | null;
  completedAt:      string | null;
  issuedAt:         string;
  items: {
    productName: string;
    quantity:    number;
    unitPrice:   number | null;
    amount:      number;
  }[];
  totalAmount: number | null;
}

/**
 * 매입 오더 상세(명세 포함). 관리자 조회는 개인정보 포함,
 * 고객 안내 앱 조회(준비 완료)는 개인정보 없이 내려온다.
 */
export interface BuybackDetail {
  id:               number;
  managementNumber: string | null;
  status:           BuybackStatus;
  statusLabel:      string;
  name:             string | null;
  phone:            string | null;
  email:            string | null;
  totalAmount:      number | null;
  items:            BuybackItem[];
  createdAt:        string;
  completedAt:      string | null;
}
